import * as React from "react"
import { useSpinDelay } from "spin-delay"
import { cn } from "#app/utils/misc.tsx"
import { Button, type ButtonProps } from "./button.tsx"
import { Icon } from "./icon.tsx"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "./tooltip.tsx"

export const StatusButton = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & {
		status: "pending" | "success" | "error" | "idle"
		message?: string | null
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}
>(({ message, status, className, children, spinDelay, ...props }, ref) => {
	const delayedPending = useSpinDelay(status === "pending", {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})
	const companion = {
		pending: delayedPending ? (
			<div className="inline-flex items-center justify-center">
				<Icon name="update" className="animate-spin" />
			</div>
		) : null,
		success: (
			<div className="inline-flex  items-center justify-center">
				<Icon name="check" />
			</div>
		),
		error: (
			<div className="inline-flex items-center justify-center rounded-full">
				<Icon name="cross-1" className="text-[14px]" />
			</div>
		),
		idle: null,
	}[status]

	return (
		<Button
			ref={ref}
			className={cn("flex items-center justify-center gap-2", className)}
			{...props}
		>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
			<div>{children}</div>
		</Button>
	)
})
StatusButton.displayName = "Button"
